import React, { useState, useEffect, useCallback } from "react";
import {
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { authenticatedAxios } from "../apis";
import { extractErrorMessage } from "../utils";
import Pagination from "../components/commons/Pagination"; // Assuming Pagination component is here

export default function Suggestions() {
  const [suggestions, setSuggestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // This state will hold the search term that is actually submitted
  const [committedSearchTerm, setCommittedSearchTerm] = useState("");

  const PAGE_SIZE = 10;

  // Function to fetch suggestions from the API
  const fetchSuggestions = useCallback(async (currentPage, search) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await authenticatedAxios.get("/api/admin/suggestions", {
        params: {
          page: currentPage,
          size: PAGE_SIZE,
          search: search,
        },
      });

      // Assuming the API response is now { data: [...], pagination: { total_pages: X } }
      const responseData = response.data;

      // Check if the response data is in the expected format

      if (responseData) {
        setSuggestions(responseData.data);
        setTotalPages(responseData.total_page || 1);
      } else {
        throw new Error("Unexpected API response format");
      }
    } catch (err) {
      console.error("Failed to fetch suggestions:", err);
      const errorMessage = extractErrorMessage(err?.response?.data || err);
      setError(errorMessage);
      setSuggestions([]); // Clear suggestions on error
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, []); // useCallback dependencies are empty as fetchSuggestions is self-contained

  // Effect to fetch data whenever the page or the committed search term changes
  useEffect(() => {
    fetchSuggestions(page, committedSearchTerm);
  }, [page, committedSearchTerm, fetchSuggestions]);

  // Function to handle the search submission
  const handleSearch = () => {
    setCommittedSearchTerm(searchTerm);
    // When a new search is performed, always go back to the first page
    if (page !== 1) {
      setPage(1);
    } else {
      // If already on page 1, the useEffect won't re-trigger, so we call fetch manually
      fetchSuggestions(1, searchTerm);
    }
  };

  // Triggers the search when the "Enter" key is pressed
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Helper function to format date strings
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between md:items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 text-left">
            Suggestion Box
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            User-submitted suggestions for development
          </p>
        </div>
      </div>

      <div className="bg-white p-4 shadow-md rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="relative w-full max-w-md">
            <input
              type="text"
              className="block w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="Search suggestions and press Enter..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <ChatBubbleLeftRightIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-left text-red-700 rounded-md text-sm">
            <p>
              <strong>Error:</strong> {error}
            </p>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">
                  Posting Date
                </th>
                <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">
                  Suggestion
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="3" className="text-center p-8">
                    <div className="flex justify-center items-center gap-2 text-gray-500">
                      <ArrowPathIcon className="h-6 w-6 animate-spin" />
                      <span>Loading Suggestions...</span>
                    </div>
                  </td>
                </tr>
              ) : suggestions.length > 0 ? (
                suggestions.map((item) => (
                  <tr key={item.id}>
                    <td className="text-left px-6 py-4 whitespace-nowrap text-gray-900 font-medium">
                      {item.user?.first_name
                        ? item.user?.first_name + " " + item.user?.last_name
                        : item.user?.username || "Anonymous"}
                    </td>
                    <td className="text-left px-6 py-4 whitespace-nowrap text-gray-600">
                      {formatDate(item.created_at)}
                    </td>
                    <td
                      className="text-left px-6 py-4 whitespace-normal text-gray-700"
                      style={{ minWidth: "300px" }}
                    >
                      {item.message}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="3"
                    className="text-center px-6 py-8 text-gray-500"
                  >
                    No suggestions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* --- Pagination Component --- */}
        {!isLoading && suggestions.length > 0 && totalPages > 1 && (
          <div className="items-center justify-center mt-4 md:mt-6 flex">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}
